import { createSlice } from '@reduxjs/toolkit'

import { logoutUser } from '@/redux/slices/authSlice'

const initialState = {
	// Deduped union of every channel the current session should subscribe to.
	channels: [],
	// The subset sourced from app settings - app level, so it survives logout.
	settingsChannels: [],
}

// Turn one name or a list into clean, non-empty, trimmed strings.
const toCleanNames = (input) => {
	const names = Array.isArray(input) ? input : [input]
	return names
		.filter((name) => typeof name === 'string')
		.map((name) => name.trim())
		.filter((name) => name.length > 0)
}

// Append only the names not already present (keeps the list unique, order-stable).
const pushUnique = (list, names) => {
	const existing = new Set(list)
	names.forEach((name) => {
		if (existing.has(name)) return
		existing.add(name)
		list.push(name)
	})
}

/*
 * Central accumulator for Pusher channel names.
 *
 * Names arrive from two kinds of source:
 *   - app settings (app level) -> tracked in `settingsChannels`, survive logout
 *   - user/profile/login responses (each carries its own channel - e.g. a guest
 *     specific one - as a top-level sibling of `user`) -> session scoped, dropped
 *     on logout
 *
 * `channels` is the deduped union of both, and is what subscribers read. On logout
 * we rebuild it from `settingsChannels` only, so a logged-in user's channel does
 * not linger after they sign out.
 *
 * Not persisted (see the store whitelist): it rebuilds from fresh API responses
 * on every page load.
 */
const pusherSlice = createSlice({
	name: 'pusher',
	initialState,
	reducers: {
		// App-level channels from settings - tracked separately so they survive logout.
		addSettingsPusherChannels(state, { payload }) {
			const names = toCleanNames(payload)
			pushUnique(state.settingsChannels, names)
			pushUnique(state.channels, names)
		},
		// Channels tied to the logged-in user/profile - cleared on logout.
		addUserPusherChannels(state, { payload }) {
			pushUnique(state.channels, toCleanNames(payload))
		},
	},
	extraReducers: (builder) => {
		// Drop the user's channels, keep the app-level ones.
		builder.addCase(logoutUser, (state) => ({
			...initialState,
			settingsChannels: [...state.settingsChannels],
			channels: [...state.settingsChannels],
		}))
	},
})

export const { addSettingsPusherChannels, addUserPusherChannels } = pusherSlice.actions

export default pusherSlice.reducer
